import type {
  ProvenanceSearchedEntry,
  ProvenanceSource,
} from "../../types/provenance";

/**
 * One collection/dataset in the Searched tier, with the queries that hit it and
 * a cited/read result summary (spec §5.1: "each collection/dataset queried …
 * and (on expand) the actual search queries used and the result summary";
 * mockup: "5 passages from 2 documents. 3 cited, 2 read only.").
 */
export interface SearchedCollectionGroup {
  key: string;
  /** Human collection name; null for queries with no named collection. */
  name: string | null;
  agent: string | null;
  /** Documents in the collection (from the search event); null if ambiguous. */
  documentCount: number | null;
  queries: {
    text: string | null;
    timestamp?: string | null;
    resultCount: number;
  }[];
  /** Cited/read passages from this collection (matched by dataset name). */
  passagesCited: number;
  passagesRead: number;
  /** Distinct documents among the cited+read passages from this collection. */
  documentsFromPassages: number;
}

const UNNAMED_KEY = "__unnamed__";

/**
 * Group per-query Searched entries into per-collection cards and attribute each
 * collection's cited/read passages. Passages are matched to a collection by
 * dataset name (the collection label the search event carries IS the dataset
 * name), so the counts line up with what the Read/Cited tiers show.
 *
 * Queries with no named collection collapse into a single unnamed group (its
 * cited/read summary is omitted — those passages can't be attributed to a
 * displayed collection).
 */
export const groupSearchedByCollection = (
  entries: ProvenanceSearchedEntry[],
  citedSources: ProvenanceSource[] = [],
  readSources: ProvenanceSource[] = [],
): SearchedCollectionGroup[] => {
  const groups = new Map<string, SearchedCollectionGroup>();

  const ensure = (name: string | null): SearchedCollectionGroup => {
    const key = name ?? UNNAMED_KEY;
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        name,
        agent: null,
        documentCount: null,
        queries: [],
        passagesCited: 0,
        passagesRead: 0,
        documentsFromPassages: 0,
      };
      groups.set(key, g);
    }
    return g;
  };

  for (const entry of entries) {
    const names = entry.collection_names?.length
      ? entry.collection_names
      : [null];
    // document_count is a per-event SUM across the query's datasets; only
    // attribute it to a collection when the query maps to exactly one (mirrors
    // the backend export's per-collection guard so it isn't multi-counted).
    const singleCollection = names.length === 1;
    for (const name of names) {
      const g = ensure(name);
      if (g.agent == null && entry.agent) g.agent = entry.agent;
      if (
        singleCollection &&
        entry.document_count != null &&
        (g.documentCount == null || entry.document_count > g.documentCount)
      ) {
        g.documentCount = entry.document_count;
      }
      g.queries.push({
        text: entry.query_text,
        timestamp: entry.timestamp,
        resultCount: entry.result_count,
      });
    }
  }

  // Attribute cited/read passages to their collection by dataset name.
  const attribute = (sources: ProvenanceSource[], cited: boolean) => {
    for (const s of sources) {
      const name = s.dataset?.name;
      if (!name) continue;
      const g = groups.get(name);
      if (!g) continue;
      if (cited) g.passagesCited += 1;
      else g.passagesRead += 1;
    }
  };
  attribute(citedSources, true);
  attribute(readSources, false);

  // Distinct documents (SDRs) behind each collection's cited+read passages.
  const docsByName = new Map<string, Set<number>>();
  for (const s of [...citedSources, ...readSources]) {
    const name = s.dataset?.name;
    const sdrId = s.source_data_record?.id;
    if (!name || sdrId == null || !groups.has(name)) continue;
    let set = docsByName.get(name);
    if (!set) docsByName.set(name, (set = new Set()));
    set.add(sdrId);
  }
  for (const [name, set] of docsByName) {
    const g = groups.get(name);
    if (g) g.documentsFromPassages = set.size;
  }

  return [...groups.values()];
};
